import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useMatch,
} from 'react-router-dom';
import AccountSettings from '@pages/account-settings';
import ChangePasswordSuccess from '@components/account-settings/changing-your-password/ChangePasswordSuccess';
import { message } from 'antd';
import { AuthProvider } from '@contexts/AuthContext';
import useAuth from '@hooks/useAuth';
import LoginPage from '@pages/login/LoginPage';
import HomePage from '@pages/homepage/HomePage';
import Header from '@components/header/index';
import ForgotPasswordPage from '@pages/forgot-password/ForgotPasswordPage';
import AddNewResident from '@pages/add-new-resident/AddNewResident';
import CreateAccountPage from '@pages/verify-account';
import PasswordPrompt from '@components/password-prompt';
import { saveWithExpiry, getWithExpiry } from '@utils/crypto';
import ResetPassword from '@pages/reset-password';
import EditResident from '@pages/edit-resident/EditResident';
import ScrollToTop from '@components/ScrollToTop';
import TeamMember from '@pages/team-member/TeamMember';
import PatientNotes from '@pages/patient-notes/PatientNotes';
import { Role } from '@typings/ProfileTypes';
import NotFoundPage from '@pages/not-found-page';
import { PartnerProvider } from '@contexts/PartnerContext';
import Footer from '@components/footer';
import Analytics from '@pages/analytics ';
import ContactPage from '@pages/contact-page';
import ContactSuccess from '@pages/confirmation/contact-successs';
import CheckYourEmail from '@components/account-settings/your-email-address/components/CheckYourEmail';
import ResendActivitationEmail from '@components/account-settings/your-email-address/components/ResendActivitationEmail';
import EmailChangedSuccess from '@components/account-settings/your-email-address/components/EmailChangedSuccess';
import VerifyEmailPage from '@components/account-settings/your-email-address/components/VerifyEmailPage';
import ActivationCodeExpired from '@components/account-settings/your-email-address/components/ActivationCodeExpired';
import Email2FAVerificationPage from '@pages/email-2fa/Email2FAVerificationPage';
import './index.scss';

let hasShownGlobalMessage = false;

function RequireAuth({
  children,
  requiredRole,
}: {
  children: any;
  // eslint-disable-next-line react/require-default-props
  requiredRole?: string;
}) {
  const { isLoggedIn, profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const isConfirmationPage = useMatch('/confirmation/:id');

  useEffect(() => {
    if (profile) {
      localStorage.setItem('role', profile?.role);
      setLoading(false);
    }
  }, [profile]);

  if (!isLoggedIn && !isConfirmationPage) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && profile && profile?.role !== requiredRole) {
    if (!hasShownGlobalMessage) {
      message.error(
        "You don't have the necessary permissions to view this page."
      );
      hasShownGlobalMessage = true;
    }
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  const [stagingPasswordAuthenticated, setStagingPasswordAuthenticated] =
    useState<boolean>(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);

  const reactEnv = process.env.REACT_APP_ENV;
  const isProduction = reactEnv === 'production';

  useEffect(() => {
    // Production: No password protection needed, go directly to login
    if (isProduction) {
      setStagingPasswordAuthenticated(true);
      setIsCheckingAuth(false);
      return;
    }

    // Staging: Check if password has been entered before
    const savedPassword = getWithExpiry('password');
    if (savedPassword === 'C@reH0m£s') {
      setStagingPasswordAuthenticated(true);
    }

    setIsCheckingAuth(false);
  }, [isProduction]);

  const handlePasswordSubmit = (password: string) => {
    if (password === 'C@reH0m£s' || process.env.REACT_APP_BASE_PASS) {
      setStagingPasswordAuthenticated(true);
      saveWithExpiry('password', password, 4 * 60 * 60 * 1000); // 4 hours in milliseconds
    } else {
      message.error('Incorrect password. Please try again.');
    }
  };

  // Show loading while checking
  if (isCheckingAuth) {
    return null; // or a loading spinner
  }

  // If staging and not authenticated with first-layer password, show password prompt
  if (!stagingPasswordAuthenticated) {
    return <PasswordPrompt onPasswordSubmit={handlePasswordSubmit} />;
  }

  // Once staging password is verified (or in production), show the actual app
  return (
    <div className="main">
      <PartnerProvider>
        <AuthProvider>
          <ScrollToTop />
          <Header />
          <div className="page-bg">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/confirmation/:uuid"
                element={<Email2FAVerificationPage />}
              />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route
                path="/account"
                element={
                  <RequireAuth>
                    <AccountSettings />
                  </RequireAuth>
                }
              />
              <Route
                path="/password-success"
                element={
                  <RequireAuth>
                    <ChangePasswordSuccess />
                  </RequireAuth>
                }
              />
              <Route
                path="/check-your-email"
                element={
                  <RequireAuth>
                    <CheckYourEmail />
                  </RequireAuth>
                }
              />
              <Route path="/update-email" element={<VerifyEmailPage />} />
              <Route
                path="/email-changed-success"
                element={<EmailChangedSuccess />}
              />
              <Route
                path="/activation-code-expired"
                element={<ActivationCodeExpired />}
              />
              <Route
                path="/resend-activation-email"
                element={
                  <RequireAuth>
                    <ResendActivitationEmail />
                  </RequireAuth>
                }
              />
              <Route
                path="/new-patient"
                element={
                  <RequireAuth>
                    <AddNewResident />
                  </RequireAuth>
                }
              />
              <Route
                path="/patient/:id"
                element={
                  <RequireAuth>
                    <EditResident />
                  </RequireAuth>
                }
              />
              <Route path="/verify" element={<CreateAccountPage />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <HomePage />
                  </RequireAuth>
                }
              />
              <Route
                path="/invite"
                element={
                  <RequireAuth requiredRole={Role.Manager}>
                    <TeamMember />
                  </RequireAuth>
                }
              />
              <Route
                path="/patient/:slug/notes"
                element={
                  <RequireAuth>
                    <PatientNotes />
                  </RequireAuth>
                }
              />
              <Route
                path="/analytics"
                element={
                  <RequireAuth>
                    <Analytics />
                  </RequireAuth>
                }
              />
              <Route
                path="/contact"
                element={
                  <RequireAuth>
                    <ContactPage />
                  </RequireAuth>
                }
              />

              <Route
                path="/contact/confirmation"
                element={
                  <RequireAuth>
                    <ContactSuccess />
                    {/* {localStorage.getItem('contact-success') === 'show' ? (
                    ) : (
                      <NotFoundPage />
                    )} */}
                  </RequireAuth>
                }
              />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
          <Footer />
        </AuthProvider>
      </PartnerProvider>
    </div>
  );
}

const AppWithRouter = () => (
  <Router>
    <App />
  </Router>
);
export default AppWithRouter;
